import {Box, Divider, Stack, Typography} from '@mui/material';
import {
	BeneficiaryCard,
	CTAButtonFixed,
	MainFooter,
	MainHeader,
	PageWrapperLayout,
	StyledGrid,
} from 'presentation/components';
import {useAccountInformation, useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import React, {Fragment} from 'react';

import {ModalError} from 'presentation/components/ModalError';
import {OperationStateTypesEnum} from 'presentation/constants';
import {Loading} from 'presentation/components/Loading/Loading';
import {
	CustomCTAButtonFixedAction,
	StyledBox,
	StyledFooterContainer,
	StyledList,
	StyledListItem,
} from './styles';
import TermsCheckbox from './components/TermsCheckbox';
import {useBusinessAccessPermission} from './hooks/useBusinessAccessPermission';

const {
	title,
	descriptionHolder,
	descriptionDependent,
	bulletActions,
	information,
	button: {authorize, notAuthorize},
} = translations['pt-br'].privateFlow.businessAccessPermission;

const {
	title: modalErrorTitle,
	description: modalErrorDescription,
	tryAgainBtn,
} = translations['pt-br'].others.genericErrorModal;

const {unableToContinue} = translations['pt-br'].commons;

export function BusinessAccessPermission() {
	const {isMobile} = useMediaQueries();

	const {isHolder} = useAccountInformation();

	const {
		termsCheckedState,
		setTermsCheckedState,
		isCheckboxRequiredState,
		AUTHORIZE_ACCESS_IS_LOADING,
		AUTHORIZE_ACCESS_IS_ERROR,
		handleBack,
		handleAuthorizeAccess,
		setOperationScheduleState,
	} = useBusinessAccessPermission();

	return (
		<PageWrapperLayout title={title}>
			<MainHeader variant="subRoute" title={title} handleBack={handleBack} hideClose />

			<StyledGrid.Root px={isMobile ? 2 : 4} spacing={2} mt={1}>
				<StyledGrid.Item sm={12} md={8} lg={8} mt={isMobile ? 1 : 0}>
					<StyledBox>
						<Typography variant="body1" color="text.primary">
							{isHolder ? descriptionHolder : descriptionDependent}
						</Typography>

						<StyledList>
							{bulletActions.map((bullet, bulletIndex) => (
								<StyledListItem key={bulletIndex}>{bullet}</StyledListItem>
							))}
						</StyledList>
						<Stack gap={'12px'}>
							<Typography variant="body1" color="text.primary">
								{information.info1}
							</Typography>
							<Typography variant="caption" color="text.secondary">
								{information.info2}
							</Typography>
						</Stack>
						{!isMobile ? (
							<Fragment>
								<Divider />
								<TermsCheckbox
									termsCheckedState={termsCheckedState}
									setTermsCheckedState={setTermsCheckedState}
									isCheckboxRequiredState={isCheckboxRequiredState}
								/>
							</Fragment>
						) : null}
					</StyledBox>
					<CTAButtonFixed.Root
						direction={isMobile ? 'column' : 'row'}
						justifyContent={'end'}
						mt={2}>
						{isMobile ? (
							<TermsCheckbox
								termsCheckedState={termsCheckedState}
								setTermsCheckedState={setTermsCheckedState}
								isCheckboxRequiredState={isCheckboxRequiredState}
							/>
						) : null}
						<CustomCTAButtonFixedAction
							fullWidth={!!isMobile}
							onClick={handleBack}
							size="large"
							variant="outlined"
							sx={{px: 7.5}}>
							{notAuthorize}
						</CustomCTAButtonFixedAction>
						<CustomCTAButtonFixedAction
							fullWidth={!!isMobile}
							onClick={handleAuthorizeAccess}
							size="large"
							variant="contained"
							sx={{px: 7.5}}
							disabled={AUTHORIZE_ACCESS_IS_LOADING}
							endIcon={
								AUTHORIZE_ACCESS_IS_LOADING ? (
									<Box width={24}>
										<Loading color="inherit" size={14} />
									</Box>
								) : null
							}>
							{authorize}
						</CustomCTAButtonFixedAction>
					</CTAButtonFixed.Root>
				</StyledGrid.Item>

				<StyledGrid.Item sm={12} md={4} lg={4}>
					<BeneficiaryCard shouldShowAnsPlanRegister={false} />
				</StyledGrid.Item>
			</StyledGrid.Root>

			{AUTHORIZE_ACCESS_IS_ERROR ? (
				<ModalError
					open={AUTHORIZE_ACCESS_IS_ERROR}
					onClose={() => setOperationScheduleState(OperationStateTypesEnum.NONE)}
					headerTitle={modalErrorTitle}
					title={unableToContinue}
					isFullScreen={false}
					maxWidth={420}
					description={modalErrorDescription}
					firstButton={{
						title: tryAgainBtn,
						handleClick: handleAuthorizeAccess,
						variant: 'contained',
					}}
				/>
			) : null}

			<StyledFooterContainer $mb={72}>
				<MainFooter />
			</StyledFooterContainer>
		</PageWrapperLayout>
	);
}
